import {
  useOrganizationsServiceOrganizationsAccessDestroy,
  useOrganizationsServiceOrganizationsAccessListKey
} from '@organization/api/queries'
import { OrganizationUsers } from '@organization/api/requests'
import { paths } from '@organization/router'
import { getNameAcronym } from '@organization/utils'
import { useInvalidateQueries, useSessionContext } from '@shared/hooks'
import { getFullName, showApiErrors } from '@shared/utils'
import {
  App,
  Avatar,
  Button,
  Card,
  Descriptions,
  DescriptionsProps,
  Flex
} from 'antd'
import { Edit, InfoCircle, Trash } from 'iconsax-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { UserRoleTag } from '../UserRoleTag'
import { UserRole } from './UserRole'

type Props = {
  data?: OrganizationUsers
}

export const UserProfile = ({ data }: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { user: userContext, isAdmin } = useSessionContext()
  const { modal } = App.useApp()

  const { t } = useTranslation()
  const navigate = useNavigate()
  const { invalidateQueries } = useInvalidateQueries()

  const isDifferentUser = data?.user?.id !== userContext?.user.id

  const { mutate: deleteUser } =
    useOrganizationsServiceOrganizationsAccessDestroy({
      onSuccess: () => {
        invalidateQueries([useOrganizationsServiceOrganizationsAccessListKey])
        navigate(paths.users.root)
      },
      onError: (e) => showApiErrors(e)
    })

  const confirmDelete = (id: number) => {
    modal.confirm({
      title: t('modal.delete_user'),
      okText: t('button.delete'),
      cancelText: t('button.cancel'),
      okType: 'danger',
      icon: <InfoCircle />,
      onOk: () => deleteUser({ id })
    })
  }

  const itemsDescription: DescriptionsProps['items'] = [
    {
      key: 'name',
      label: t('table.username'),
      children: getFullName(data?.user) ?? '-'
    },
    {
      key: 'email',
      label: t('table.email'),
      children: data?.user?.email ?? '-'
    },
    {
      key: 'identification_number',
      label: t('identification_number'),
      children: data?.user?.identification_number ?? '-'
    },
    {
      key: 'phone',
      label: t('table.phone'),
      children: data?.user?.phone ?? '-'
    },
    {
      key: 'role',
      label: t('table.role'),
      children: <UserRoleTag role={data?.role} />
    }
  ]

  const userActions = () => {
    return isAdmin && (isDifferentUser || !isAdmin)
      ? [
          <Flex gap={8} justify="end" key={1}>
            {!isAdmin && (
              <Button
                onClick={() => setIsModalOpen(true)}
                icon={<Edit size={14} />}
                type="primary"
              >
                {t('button.edit')}
              </Button>
            )}
            {isDifferentUser && (
              <Button
                onClick={() => confirmDelete(Number(data?.id))}
                icon={<Trash size={14} />}
                danger
                type="primary"
              >
                {t('button.delete')}
              </Button>
            )}
          </Flex>
        ]
      : undefined
  }

  return (
    <>
      <Card title={t('users.info')} actions={userActions()}>
        <Flex gap={20}>
          <div>
            <Avatar shape="circle" size={72} src={data?.user?.photo}>
              {getNameAcronym(
                `${data?.user?.last_name} ${data?.user?.first_name}`
              )}
            </Avatar>
          </div>

          <Descriptions column={5} layout="vertical" items={itemsDescription} />
        </Flex>
      </Card>

      {isModalOpen && (
        <UserRole user={data} onCancel={() => setIsModalOpen(false)} />
      )}
    </>
  )
}
